<template>
  <div class="word-cloud-container">
    <div class="word-cloud-header">
      <div class="word-cloud-title">
        <img src="/cloud.svg" alt="cloud" />
        <span>Nuvem de palavras</span>
      </div>
      <span v-if="days > 0" class="word-cloud-subtitle">
        Termos mais citados durante os últimos {{ days }} dias
      </span>
      <span v-if="days === 0" class="word-cloud-subtitle"> Todos os Termos mais citados </span>
    </div>

    <CustomSkeleton
      v-if="isSearching"
      :listWidth="{ firstLine: '400px', secondLine: '300px' }"
      :listHeight="{ thirdLine: '100px' }"
      type="list"
    />

    <div v-else-if="processedWords.length === 0" class="word-cloud-empty">
      <span class="no-data-text"> Não há nuvem de palavras nessa data. </span>
    </div>

    <div v-else ref="cloudContainer" class="word-cloud">
      <canvas
        ref="cloudCanvas"
        :width="containerWidth"
        :height="containerHeight"
        class="word-cloud-canvas"
        style="display:block; margin:0 auto; background:transparent;"
      ></canvas>
    </div>
  </div>
</template>

<script>
import CustomSkeleton from '@/Shared/CustomSkeleton'
import cloud from 'd3-cloud'

export default {
  name: 'WordCloud',
  components: { CustomSkeleton },
  props: {
    isSearching: {
      type: Boolean,
      default: false,
    },
    days: {
      type: Number,
      default: 0,
    },
    wordClouds: {
      type: Array,
      default: () => [],
    },
    maxWords: {
      type: Number,
      default: 50,
    },
  },
  data() {
    return {
      colors: [
        '#fc8eb4', '#979df5', '#fdc738', '#2674ff', '#d27826',
        '#2ddb91', '#9747FF', '#0ea5e9', '#f43f5e', '#10b981',
        '#f97316', '#0891b2',
      ],
      containerWidth: 500,
      containerHeight: 300,
      cloudWords: [], // Para possível hit-test
    }
  },
  computed: {
    processedWords() {
      if (!this.wordClouds || this.wordClouds.length === 0) return []
      const filteredWords = this.wordClouds
        .filter((item) => this.isValidKeyword(item.keyword))
        .slice(0, this.maxWords)
      const maxMentions = Math.max(...filteredWords.map((item) => item.mentions), 1)
      return filteredWords.map((item, index) => {
        const normalizedSize = 12 + (item.mentions / maxMentions) * 24 // fonte entre 12 e 36
        return {
          text: item.keyword.charAt(0).toUpperCase() + item.keyword.slice(1),
          value: item.mentions,
          size: normalizedSize,
          color: this.colors[index % this.colors.length],
          opacity: 0.7 + (item.mentions / maxMentions) * 0.3,
        }
      })
    },
  },
  mounted() {
    this.updateContainerSize()
    this.generateCloud()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    isValidKeyword(keyword) {
      if (!keyword) return false
      const trimmedKeyword = keyword.trim()
      const invalidKeywords = ['️', ' ', '']
      return trimmedKeyword.length > 0 && !invalidKeywords.includes(trimmedKeyword)
    },
    handleWordClick(word) {
      this.$emit('word-selected', word.keyword)
    },
    handleResize() {
      this.updateContainerSize()
      this.generateCloud()
    },
    updateContainerSize() {
      this.$nextTick(() => {
        if (!this.$refs.cloudContainer) return
        this.containerWidth = this.$refs.cloudContainer.offsetWidth || 500
        this.containerHeight = this.$refs.cloudContainer.offsetHeight || 300
        this.generateCloud()
      })
    },
    generateCloud() {
      // Debug: processedWords
      console.log('[WordCloud] processedWords:', this.processedWords)
      if (!this.processedWords.length) {
        this.cloudWords = []
        this.clearCanvas();
        return
      }
      // Debug: container size
      console.log('[WordCloud] Canvas size:', this.containerWidth, this.containerHeight)
      const layout = cloud()
        .size([this.containerWidth, this.containerHeight])
        .words(this.processedWords.map(word => ({
          text: word.text,
          size: word.size,
          color: word.color,
          opacity: word.opacity,
          value: word.value,
        })))
        .padding(10)
        .rotate(() => Math.floor(Math.random() * 2) * 90)
        .font('Impact')
        .fontSize(d => d.size)
        .on('end', words => {
          console.log('[WordCloud] Palavras posicionadas:', words)
          this.cloudWords = words
          // Garante que o canvas existe antes de desenhar
          this.$nextTick(() => {
            this.drawCloud(words)
          })
        })
      layout.start()
    },
    clearCanvas() {
      const canvas = this.$refs.cloudCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, this.containerWidth, this.containerHeight)
    },
    drawCloud(words) {
      const canvas = this.$refs.cloudCanvas
      if (!canvas) {
        console.warn('[WordCloud] Canvas não encontrado!')
        return
      }
      if (!words || !words.length) {
        console.warn('[WordCloud] Nenhuma palavra para desenhar!')
        return
      }
      if (!this.containerWidth || !this.containerHeight) {
        console.warn('[WordCloud] Dimensões do canvas inválidas!', this.containerWidth, this.containerHeight)
        return
      }
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        console.error('[WordCloud] Não foi possível obter contexto 2D do canvas!')
        return
      }
      ctx.clearRect(0, 0, this.containerWidth, this.containerHeight)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      words.forEach((word, i) => {
        ctx.save()
        ctx.font = `${word.size}px Arial, sans-serif` // fonte garantida
        ctx.fillStyle = word.color || '#111'
        ctx.globalAlpha = 1 // força opacidade
        ctx.translate(word.x + this.containerWidth / 2, word.y + this.containerHeight / 2)
        ctx.rotate(word.rotate * Math.PI / 180)
        ctx.fillText(word.text, 0, 0)
        ctx.restore()
      })
    }
  },
  watch: {
    wordClouds() {
      this.$nextTick(() => {
        this.updateContainerSize()
        this.generateCloud()
      })
    },
  },
}
</script>

<style lang="scss" src="./styles.scss"></style>
